/// <amd-module name="Notifications/Common/Services/FollowsService" />

import { Services, VssObservableService } from "VSS/Platform/Context";
import { IVssPageService, IPageData } from "VSS/Platform/Page";
import { INotificationRestClient } from "RestClient/Notification";
import {
    SubscriptionQuery,
    SubscriptionQueryCondition,
    SubscriptionQueryFlags,
    BaseSubscriptionFilter,
    ISubscriptionFilter,
    SubscriptionFlags,
    NotificationSubscription,
    SubscriberFlags,
    NotificationSubscriber,
    ArtifactFilter
} from "Contracts/Notification";
import { IFollowsService } from "Contracts/FollowsService";
import { decodeUri } from "VSS/Platform/Util/Linking";

/**
 * interface defining arguments for following an artifact.
 */
export interface IFollowedArtifact {
    type: string;
    id: string;
    subscriptionId?: string;
}

export enum FollowsAction {
    Follow = "Follow",
    Unfollow = "Unfollow"
}

export interface IFollowArtifactChanged extends IFollowedArtifact {
    action: FollowsAction;
}

export namespace KnownArtifactTypes {
    export const WorkItem: string = "WorkItem";
}

class FollowsService extends VssObservableService<IFollowArtifactChanged> implements IFollowsService {

    private _cachedFollowedArtifactsPromises: { [key: string]: Promise<void> } = {};
    private _cachedFollowedArtifacts: { [key: string]: { [key: string]: IFollowedArtifact } } = {};
    private static readonly FollowsType = "Artifact";
    private static readonly PreviousCachedArtifactSuffix = "_old";

    public refresh(artifactType: string): Promise<void> {
        return this.ensureArtifacts(artifactType, true);
    }

    public isFollowing(artifactType: string, artifactId: string): Promise<boolean> {

        const cachedFollowedArtifacts: { [key: string]: IFollowedArtifact } = this._cachedFollowedArtifacts[artifactType];

        if (cachedFollowedArtifacts) {
            return Promise.resolve(this.isFollowingFromCache(artifactType, artifactId));
        }

        return this.ensureArtifacts(artifactType).then(() => {
            return this.isFollowingFromCache(artifactType, artifactId);
        });
    }

    public follow(artifactType: string, artifactId: string, description?: string): Promise<void> {

        return this.ensureArtifacts(artifactType).then(() => {

            const artifactSubscription: IFollowedArtifact | null = this.getSubscriptionFromCache(artifactType, artifactId);

            if (artifactSubscription) {
                // already following, just resolve the promise.
                return Promise.resolve();
            }

            const client: INotificationRestClient =
                this.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient");

            const subscription: NotificationSubscription = this.createSubscriptionObject(artifactType, artifactId, description);
            const promise: Promise<NotificationSubscription> = client.createSubscription(subscription);

            return promise.then((subscription: NotificationSubscription) => {
                const followedArtifact: IFollowedArtifact = this.getFollowedArtifactFromSubscription(artifactType, subscription);
                this.addFollowedArtifact(followedArtifact);
            }, (reason) => {
                // do nothing on failure (do not update the cache state).
            });
        });
    }

    public unfollow(artifactType: string, artifactId: string): Promise<void> {

        return this.ensureArtifacts(artifactType).then(() => {
            const followedArtifact: IFollowedArtifact | null = this.getSubscriptionFromCache(artifactType, artifactId);

            // if we aren't already following this artifact, do nothing.
            if (!followedArtifact) {
                return Promise.resolve();
            }

            return this.getSubscriptionId(followedArtifact).then((id: string | null) => {
                if (id) {
                    return this.deleteFromServerWithSubscriptionId(followedArtifact);
                }
            }, (reason: any) => {
                // todo:  Log telemetry here.
            });
        });
    }

    public setData(artifactType: string, artifactIds: string[]): void {

        const originalFollowedArtifacts: { [key: string]: IFollowedArtifact } =
            this._cachedFollowedArtifacts[artifactType] || {};

        const newFollowedArtifacts: { [key: string]: IFollowedArtifact } = {};
        this._cachedFollowedArtifacts[artifactType] = newFollowedArtifacts;

        if (artifactIds) {
            artifactIds.forEach((id: string) => {

                let originalArtifact: IFollowedArtifact = originalFollowedArtifacts[id];

                if (originalArtifact) {
                    // re-use the original one since it may have the subscription id.
                    newFollowedArtifacts[id] = originalArtifact;
                }
                else {
                    newFollowedArtifacts[id] = {
                        id: id,
                        type: artifactType
                    };

                    // this is an add of a followed item.
                    this.fireEvent(newFollowedArtifacts[id], FollowsAction.Follow);
                }
            });
        }

        // fire events for the removes
        for (let id in originalFollowedArtifacts) {
            if (!newFollowedArtifacts[id]) {
                // it used to exist but no longer does, this is a deletion.
                this.fireEvent(originalFollowedArtifacts[id], FollowsAction.Unfollow);
            }
        }
    }

    public async getSubscription(artifactType: string, artifactId: string): Promise<NotificationSubscription | null> {
        const followedArtifact: IFollowedArtifact | null = this.getSubscriptionFromCache(artifactType, artifactId);

        if(!followedArtifact){
            return null;
        }

        const subscriptionId = await this.getSubscriptionId(followedArtifact);

        // If subscriptoinId is null then it is likely unfollowed on the server. Update the cache
        if(!subscriptionId){
            this.deleteFollowedArtifact(followedArtifact);
            return null;
        }

        const client: INotificationRestClient = this.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient");

        return client.getSubscription(subscriptionId);
    }

    /**
     * Gets the subscription ID from the notification service for the specified artifact
     */
    private getSubscriptionId(followedArtifact: IFollowedArtifact): Promise<string | null> {

        if (followedArtifact.subscriptionId) {
            Promise.resolve(followedArtifact.subscriptionId);
        }

        const subQuery: SubscriptionQuery | null = this.getSubscriptionQuery(followedArtifact.type, followedArtifact.id);

        if (!subQuery) {
            return Promise.resolve(null);
        }

        const client: INotificationRestClient =
            this.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient");

        return client.querySubscriptions(subQuery).then((subscriptions: NotificationSubscription[]) => {
            if (subscriptions.length > 0) {
                followedArtifact.subscriptionId = subscriptions[0].id;
                return followedArtifact.subscriptionId;
            }
            else {
                return null;
            }
        });
    }

    /**
     * Deletes the follows subscription from the server.  Followed artifact must have a subscriptionId for
     * this to function.
     */
    private deleteFromServerWithSubscriptionId(followedArtifact: IFollowedArtifact): Promise<void> {
        const client: INotificationRestClient =
            this.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient");

        if (followedArtifact.subscriptionId) {
            const promise: Promise<void> = client.deleteSubscription(followedArtifact.subscriptionId);

            return promise.then(() => {
                this.deleteFollowedArtifact(followedArtifact);
            }, () => {
                // probably not an error, just means it's already unfollowed in most cases.
                this.deleteFollowedArtifact(followedArtifact);
                return Promise.resolve();
            });
        }
        return Promise.reject("Must have subscription id present to call this method.");
    }

    /**
     * Gets the current user's id from the page context, or null if it cannot get it.
     */
    private getUserId(): string | null {
        const pageService: IVssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined = pageService.getData();

        if (pageData) {
            return pageData.user.id;
        }
        else {
            // something is broken if we don't have a user id.
            return null;
        }
    }

    /**
     * Retrieves followed artifacts from the server.
     * @param artifactType Type of artifact to retrieve
     * @param force Whether or not to ignore the cache.
     */
    private ensureArtifacts(artifactType: string, force?: boolean): Promise<void> {

        const promise: Promise<void> = this._cachedFollowedArtifactsPromises[artifactType];
        if (promise) {
            // already fetching this data.
            return promise;
        }

        if (this._cachedFollowedArtifacts[artifactType] && !force) {
            // already have the data and 'force' is not set.
            return Promise.resolve();
        }

        if (this._cachedFollowedArtifacts[artifactType]) {
            // store the current set in another key so we can fire events for the
            // items that were added/removed once the request completes
            this._cachedFollowedArtifacts[artifactType + FollowsService.PreviousCachedArtifactSuffix] =
                this._cachedFollowedArtifacts[artifactType];

            delete this._cachedFollowedArtifacts[artifactType];
        }

        const subQuery: SubscriptionQuery | null = this.getSubscriptionQuery(artifactType, undefined);

        if (!subQuery) {
            return Promise.reject("could not construct sub-query");
        }

        const client: INotificationRestClient =
            this.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient");

        const newPromise: Promise<void> = client.querySubscriptions(subQuery).then((subscriptions: NotificationSubscription[]) => {
            delete this._cachedFollowedArtifactsPromises[artifactType];
            this.processResults(artifactType, subscriptions);
        });

        this._cachedFollowedArtifactsPromises[artifactType] = newPromise;

        return newPromise;
    }

    /**
     * Gets a subscription query filled in with the specified parameters
     * @param artifactType Type of artifact to query for
     * @param artifactId Specific artifact id, if any.  If not specified will return all subscriptions for the
     *                      specified artifact type.
     */
    private getSubscriptionQuery(artifactType: string, artifactId?: string): SubscriptionQuery | null {

        // get the current user id.
        let userId: string | null = this.getUserId();

        if (!userId) {
            return null;
        }

        const wif: ArtifactFilter = {
            artifactId: <any>artifactId,
            artifactType: artifactType,
            artifactUri: <any>undefined,
            type: FollowsService.FollowsType,
            eventType: <any>undefined
        };

        const wic: SubscriptionQueryCondition = {
            subscriptionId: <any>null,
            filter: wif,
            scope: <any>null,
            subscriberId: userId,
            flags: <any>null
        };

        return { conditions: [wic], queryFlags: SubscriptionQueryFlags.None };
    }

    /**
     * Creates a new subscription object to follow a specific artifact
     */
    private createSubscriptionObject(
        artifactType: string,
        artifactId: string,
        description?: string): NotificationSubscription {

        // using 'any' casts here to allow null to be assigned.  Generated types
        // are not allowing nulls.
        const filter: ArtifactFilter = {
            artifactId: artifactId,
            artifactType: artifactType,
            type: FollowsService.FollowsType,
            artifactUri: <any>null,
            eventType: <any>null,
        };

        const notificationSubscription: NotificationSubscription = {
            diagnostics: <any>null,
            _links: null,
            adminSettings: <any>null,
            channel: <any>null,
            flags: <any>null,
            lastModifiedBy: <any>null,
            modifiedDate: <any>null,
            scope: <any>null,
            status: <any>null,
            statusMessage: "",
            subscriber: <any>this.getUserId(),
            url: "",
            userSettings: <any>null,
            description: description ?? "",
            extendedProperties: <any>null,
            id: <any>undefined,
            filter: filter,
            permissions: <any>null
        };

        return <NotificationSubscription>notificationSubscription;
    }

    /**
     * Retrieves whether or not a specific artifact is being followed.  Only operates on the cache.
     */
    private isFollowingFromCache(artifactType: string, artifactId: string): boolean {
        return this.getSubscriptionFromCache(artifactType, artifactId) != null;
    }

    /**
     * Retrieves a specific subscription from the cache, null if it doesn't exist.
     */
    private getSubscriptionFromCache(artifactType: string, artifactId: string): IFollowedArtifact | null {
        const cachedFollowedArtifacts: { [key: string]: IFollowedArtifact } = this._cachedFollowedArtifacts[artifactType];

        if (cachedFollowedArtifacts) {
            return cachedFollowedArtifacts[artifactId];
        }
        return null;
    }

    /**
     * Handles the results of the query for subscriptions by artifact type, putting them in the cache and firing events as needed.
     */
    private processResults(artifactType: string, subscriptions: NotificationSubscription[]): void {
        if (this._cachedFollowedArtifacts[artifactType]) {
            return;
        }

        this._cachedFollowedArtifacts[artifactType] = {};

        const idToFollowedArtifact: { [key: string]: IFollowedArtifact } = {};
        subscriptions.forEach((subscription: NotificationSubscription) => {
            const followedArtifact: IFollowedArtifact = this.getFollowedArtifactFromSubscription(artifactType, subscription);
            idToFollowedArtifact[followedArtifact.id] = followedArtifact;
            this.addFollowedArtifact(followedArtifact);
        });

        // process deletions.  Find all the items in the 'original' cached collection
        // that don't exist in the 'new' collection and fire events for them.
        const originalFollowedArtifacts: { [key: string]: IFollowedArtifact } =
            this._cachedFollowedArtifacts[artifactType + FollowsService.PreviousCachedArtifactSuffix] || {};
        const currentFollowedArtifacts: { [key: string]: IFollowedArtifact } = this._cachedFollowedArtifacts[artifactType];

        for (let artifactId in originalFollowedArtifacts) {
            if (!currentFollowedArtifacts[artifactId]) {
                this.fireEvent(originalFollowedArtifacts[artifactId], FollowsAction.Unfollow);
            }
        }

        // we have no more need for the old artifacts.
        delete this._cachedFollowedArtifacts[artifactType + FollowsService.PreviousCachedArtifactSuffix];
    }

    /**
     * Adds a single artifact to the cache, firing the notification
     */
    private addFollowedArtifact(artifact: IFollowedArtifact): void {
        let artifactCache: { [key: string]: IFollowedArtifact } =
            this._cachedFollowedArtifacts[artifact.type];

        if (!artifactCache) {
            artifactCache = {};
            this._cachedFollowedArtifacts[artifact.type] = artifactCache;
        }

        artifactCache[artifact.id] = artifact;

        // fire the 'add' event iff this is a new addition, not due to a forced refresh where this subscription already existed.
        const originalCachedArtifacts: { [key: string]: IFollowedArtifact } =
            this._cachedFollowedArtifacts[artifact.type + FollowsService.PreviousCachedArtifactSuffix] || {};

        if (!originalCachedArtifacts[artifact.id]) {
            this.fireEvent(artifact, FollowsAction.Follow);
        }
    }

    /**
     * Removes a single artifact from the cache.
     */
    private deleteFollowedArtifact(artifact: IFollowedArtifact): void {
        const cachedFollowedArtifacts: { [key: string]: IFollowedArtifact } = this._cachedFollowedArtifacts[artifact.type];

        if (cachedFollowedArtifacts) {
            const followedArtifact: IFollowedArtifact = cachedFollowedArtifacts[artifact.id];

            if (followedArtifact) {
                delete cachedFollowedArtifacts[artifact.id];

                this.fireEvent(followedArtifact, FollowsAction.Unfollow);
            }
        }
    }

    /**
     * Extracts an IFollowedArtifact from a NotificationSubscription object.
     */
    private getFollowedArtifactFromSubscription(artifactType: string, subscription: NotificationSubscription): IFollowedArtifact {
        const artifactUri: string = <string>(<ArtifactFilter>subscription.filter).artifactUri;
        const id: string = decodeUri(artifactUri).id;
        const subscriptionId: string = subscription.id;

        return {
            id: id,
            type: artifactType,
            subscriptionId: subscriptionId
        };
    }

    /**
     * Fires notifications
     */
    private fireEvent(artifact: IFollowedArtifact, action: FollowsAction): void {
        const event: IFollowArtifactChanged = {
            action: action,
            id: artifact.id,
            subscriptionId: artifact.subscriptionId,
            type: artifact.type
        };

        // this will notify all observers who are not filtering by 'action' plus any
        // observers that filter to precisely this artifact.
        const artifactAction: string = `${artifact.type}_${artifact.id}`;
        this._notify(event, artifactAction);
    }
}

Services.add("IFollowsService", { serviceFactory: FollowsService });
